import { useCallback, useState } from 'react';

import logger from 'src/util/logger';

/**
 * Shared error / retry / cancellation state machine for batched data-fetching hooks.
 *
 * Manages the `error`, `isRetrying`, and `retryNonce` lifecycle that is common to
 * `useTradeData` and `usePropertyData`. Feature-specific side effects (e.g. clearing
 * `combinedData`) remain in the consuming hook; only the error-state transitions are
 * centralised here.
 * @param {object} [params]
 * @param {Function} [params.onErrorDispatch] - Optional callback invoked after error state
 * is set. Use it to dispatch the feature's reducer action (e.g. CANCEL_REQUEST).
 * @returns {object} Error state and handlers:
 *   - error: {Error|null} The last fetch error.
 *   - isRetrying: {boolean} True while a replay fetch is in flight.
 *   - retryNonce: {number} Increment to trigger a fresh fetch in the consuming effect.
 *   - queryType: {string|null} Derived from `error?.queryType`.
 *   - clearError: {Function} Clears error and isRetrying without retrying.
 *   - handleRetry: {Function} Resets error state, sets isRetrying, increments retryNonce.
 *   - handleFetchError: {Function} Logs the error, updates state, calls onErrorDispatch.
 */
const useBatchedFetchErrorState = ({ onErrorDispatch } = {}) => {
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  /**
   * Clears the error state without triggering a refetch.
   * Used after successfully restoring a previous snapshot so the error UI
   * disappears without starting a new network request.
   */
  const clearError = useCallback(() => {
    setError(null);
    setIsRetrying(false);
  }, []);

  /**
   * Clears only the error, leaving `isRetrying` untouched. Used by consuming
   * hooks' fetch effects to discard a stale error when a new fetch starts due
   * to a dependency change, without disrupting an in-flight retry's flag.
   */
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clears `isRetrying` when a fetch or cache hit resolves successfully.
   * The consuming hook's data-received and cache-hit paths call this so the
   * retrying indicator is cleared as soon as fresh data is available.
   */
  const resolveRetry = useCallback(() => {
    setIsRetrying(false);
  }, []);

  /**
   * Replays the entire fetch pipeline by bumping `retryNonce`, which consuming
   * hooks include as a `useEffect` dependency. `isRetrying` is cleared by
   * `handleFetchError` on failure or by `resolveRetry` on success.
   */
  const handleRetry = useCallback(() => {
    setError(null);
    setIsRetrying(true);
    setRetryNonce((n) => n + 1);
  }, []);

  /**
   * Records a fetch error: logs it, updates error state, and notifies the
   * feature-level reducer via the injected `onErrorDispatch` callback.
   * @param {Error} fetchError - The error thrown by the fetch pipeline.
   */
  const handleFetchError = useCallback((fetchError) => {
    logger.error('Error fetching batched data:', fetchError);
    setIsRetrying(false);
    setError(fetchError);
    if (onErrorDispatch) {
      onErrorDispatch(fetchError);
    }
  }, [onErrorDispatch]);

  return {
    error,
    isRetrying,
    retryNonce,
    queryType: error?.queryType ?? null,
    clearError,
    resetError,
    resolveRetry,
    handleRetry,
    handleFetchError,
  };
};

export default useBatchedFetchErrorState;
